import {
  AFFINE_AI_PANEL_WIDGET,
  type AffineAIPanelWidget,
  type AffineSlashMenuActionItem,
  type AffineSlashMenuContext,
  type AffineSlashMenuItem,
  AffineSlashMenuWidget,
  type AffineSlashSubMenu,
  type AIItemConfig,
  AIStarIcon,
  DocModeProvider,
  MoreHorizontalIcon,
} from '@blocksuite/affine/blocks';
import { assertExists } from '@blocksuite/affine/global/utils';
import { html } from 'lit';

import { pageAIGroups } from '../../_common/config';
import { handleInlineAskAIAction } from '../../actions/doc-handler';
import { AIProvider } from '../../provider';

export function setupSlashMenuAIEntry(slashMenu: AffineSlashMenuWidget) {
  const AIItems = pageAIGroups.map(group => group.items).flat();

  const iconWrapper = (icon: AIItemConfig['icon']) => {
    return html`<div style="color: var(--affine-primary-color)">
      ${typeof icon === 'function' ? html`${icon()}` : icon}
    </div>`;
  };

  const showWhenWrapper =
    (item?: AIItemConfig) =>
    ({ rootComponent }: AffineSlashMenuContext) => {
      const affineAIPanelWidget = rootComponent.host.view.getWidget(
        AFFINE_AI_PANEL_WIDGET,
        rootComponent.model.id
      );
      if (affineAIPanelWidget === null) return false;

      const chain = rootComponent.host.command.chain();
      const docModeService = rootComponent.std.get(DocModeProvider);
      const editorMode = docModeService.getPrimaryMode(rootComponent.doc.id);

      return item?.showWhen?.(chain, editorMode, rootComponent.host) ?? true;
    };

  const actionItemWrapper = (
    item: AIItemConfig
  ): AffineSlashMenuActionItem => ({
    ...basicItemConfig(item),
    action: ({ rootComponent }: AffineSlashMenuContext) => {
      item?.handler?.(rootComponent.host);
    },
  });

  const subMenuWrapper = (item: AIItemConfig): AffineSlashSubMenu => {
    assertExists(item.subItem);
    return {
      ...basicItemConfig(item),
      subMenu: item.subItem.map<AffineSlashMenuActionItem>(
        ({ type, handler }) => ({
          name: type,
          action: ({ rootComponent }) => handler?.(rootComponent.host),
        })
      ),
    };
  };

  const basicItemConfig = (item: AIItemConfig) => {
    return {
      name: item.name,
      icon: iconWrapper(item.icon),
      alias: ['ai'],
      showWhen: showWhenWrapper(item),
    };
  };

  const menu = slashMenu.config.items.slice();
  menu.unshift({
    name: 'AI助手',
    icon: AIStarIcon,
    showWhen: showWhenWrapper(),
    action: ({ rootComponent }) => {
      const view = rootComponent.host.view;
      const affineAIPanelWidget = view.getWidget(
        AFFINE_AI_PANEL_WIDGET,
        rootComponent.model.id
      ) as AffineAIPanelWidget;
      assertExists(affineAIPanelWidget);
      assertExists(AIProvider.actions.chat);
      assertExists(affineAIPanelWidget.host);
      handleInlineAskAIAction(affineAIPanelWidget.host);
    },
  });

  // const AIMenuItems: AffineSlashMenuItem[] = [
  //   { groupName: 'TechGPT AI' },
  //   ...AIItems.filter(({ name }) =>
  //     ['Fix spelling', 'Fix grammar'].includes(name)
  //   ).map(item => ({
  //     ...actionItemWrapper(item),
  //     name: `${item.name} from above`,
  //   })),
  //
  //   ...AIItems.filter(({ name }) =>
  //     ['Summarize', 'Continue writing'].includes(name)
  //   ).map(actionItemWrapper),
  //
  //   {
  //     name: 'Action with above',
  //     subMenu: [
  //       { groupName: 'Action with above' },
  //       ...AIItems.filter(({ name }) =>
  //         ['Translate to', 'Change tone to'].includes(name)
  //       ).map(subMenuWrapper),
  //
  //       ...AIItems.filter(({ name }) =>
  //         [
  //           'Improve writing',
  //           'Make it longer',
  //           'Make it shorter',
  //           'Generate outline',
  //           'Find actions',
  //         ].includes(name)
  //       ).map(actionItemWrapper),
  //     ],
  //   },
  // ];

  // const AIMenuItems: AffineSlashMenuItem[] = [
  //   { groupName: 'TechGPT AI' },
  //   {
  //     name: 'Action with above',
  //     subMenu: [
  //       { groupName: 'Action with above' },
  //       {
  //         name: 'Action with above',
  //       },
  //     ],
  //   },
  // ];

  const basicGroupEnd = menu.findIndex(
    item => 'groupName' in item && item.groupName === 'List'
  );
  // insert ai item after basic group
  menu.splice(basicGroupEnd, 0);

  const overrideConfig = {
    Attachment: {
      name: '附件',
      description: '添加附件',
    },
    Image: {
      name: '图片',
      description: '添加图片',
    },
    Equation: {
      name: '公式',
      description: '添加公式',
    },
    Text: {
      name: '正文',
      description: '',
    },
    Bold: {
      name: '黑体',
      description: '',
    },
    Italic: {
      name: '斜体',
      description: '',
    },
    Underline: {
      name: '下划线',
      description: '',
    },
    Strikethrough: {
      name: '删除线',
      description: '',
    },
    'Table View': {
      name: '添加表格',
      description: '',
    },
    'Kanban View': {
      name: '添加看板',
      description: '',
    },
    'Code Block': {
      name: '代码',
      description: '',
    },
    'Heading 1': {
      name: '标题1',
      description: '',
    },
    'Heading 2': {
      name: '标题2',
      description: '',
    },
    'Heading 3': {
      name: '标题3',
      description: '',
    },
    'Other Headings': {
      name: '其他标题',
      description: '',
    },
    'Heading 4': {
      name: '标题4',
      description: '',
    },
    'Heading 5': {
      name: '标题5',
      description: '',
    },
    'Heading 6': {
      name: '标题6',
      description: '',
    },
    'Bulleted List': {
      name: '项目符号',
      description: '',
    },
    'Numbered List': {
      name: '数字编号',
      description: '',
    },
    'To-do List': {
      name: '待办事项',
      description: '',
    },
    // 可扩展更多菜单项的重写配置
  };
  const groupsToFilter = ['Date','Actions','Page']; // 要过滤的组名
  const itemsToFilter = ['YouTube', 'GitHub', 'Figma', 'Loom','Link',
    'Move Up','Move Down','Copy','Duplicate','Delete','New Doc',
    'Linked Doc','Quote','Divider','Inline equation','Todo','Other Headings']; // 要过滤的菜单项

  slashMenu.config = {
    ...AffineSlashMenuWidget.DEFAULT_CONFIG,
    //items: menu,
    items: menu
    .filter(item => {
      return !itemsToFilter.includes(item.name)})
    .filter(item => {
        // 判断 groupName 是否为空值（undefined/null/""）
        const isGroupNameEmpty = !item.groupName;
        // 判断 name 是否为空值（undefined/null/""）
        const isNameEmpty = !item?.name;
        // 保留：不满足“两者都为空”的项（即过滤掉两者都为空的项）
        return !(isGroupNameEmpty && isNameEmpty);
      })
    .filter(item => !groupsToFilter.includes(item.groupName))
    .map(item => { // 再对剩余项应用重写配置
      const overrides = overrideConfig[item.name];
      return overrides ? { ...item, ...overrides } : item;
    }),
  };
}
